import { Component, Input, AfterViewInit, OnDestroy, OnChanges, EventEmitter, Output, signal } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { catchError, map } from 'rxjs';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { Flow } from 'src/app/perical/models/flow.model';
import { Fluid, FluidType } from 'src/app/perical/models/fluid.model';
import { MudPit, OperationType, RunCasingAndCirculate } from 'src/app/perical/models/thermal-operation.model';
import { GetShortLengthValueFromInches } from 'src/app/perivis/shared/helpers/units.helper';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { TubularString } from 'src/app/wellbore-inputs/models/well-schematic.model';
import { DrillstringService } from 'src/app/wellbore-inputs/services/drillstring.service';
export interface RunCasingAndCirculateForm {
  type: OperationType;
  fluid: Fluid;
  standardFluidFlow: Flow;
  tripIntoHole: number;
  circulationTime: number;
  includeRiser: boolean;
  outsideDiameter: number;
  insideDiameter: number;
  inletTemperature: number;
  staticPeriod: number;
  pipeRoughness: number;
  useMudPits: boolean;
  mudPits: any;
  annulusIndex: number;
}

@Component({
    selector: 'app-run-casing-and-circulate-details',
    templateUrl: './run-casing-and-circulate-details.component.html',
    providers: subformComponentProviders(RunCasingAndCirculateDetailsComponent),
    standalone: false
})
export class RunCasingAndCirculateDetailsComponent implements OnChanges, OnDestroy, AfterViewInit {

  constructor(
    private _drillstringService: DrillstringService,
    private _signalRService: SignalRService
  ) { }

  public fluids: Fluid[];
  @Input('fluids') set _fluids(fluids: Fluid[]) {
    this.fluids = fluids?.filter(f => f.state.type === FluidType.BRINE ||
      f.state.type === FluidType.STANDARDMUD ||
      f.state.type === FluidType.ADVANCEDMUD)
      .sort((a, b) => a.state.name.localeCompare(b.state.name));
  }

  public annularFluidId: string;
  @Input('annularFluidId') set _annularFluidId(annularFluidId: string) {
    this.annularFluidId = annularFluidId ?? null;
  }

  @Input()
  public isReadOnly: boolean;

  @Input()
  public isSubsea: boolean;

  @Input()
  public surfaceTemperature: number;

  @Input()
  public userUnits: UserUnitsModel;

  @Input()
  public currentString: TubularString;

  @Input()
  public defaultRiser: any;

  @Input()
  currentTubularIndex: number;

  @Input()
  public previousOperationMudPits: MudPit;

  @Input()
  public triggerMudPitTempChange: boolean;

  @Output()
  selectedTabChange = new EventEmitter<string>();

  public operationType = 'runCasingAndCirculate';
  public FluidType: typeof FluidType = FluidType;
  public isLoading = signal<boolean>(false);
  public showLandingString: boolean;
  public landingStringMissing: boolean;
  public landingString: any;
  public selectedLandingString: string;
  public showRiser: boolean;
  public shortLengthUnit: string;
  public weigthUnit: string;
  public tempUnit: string;
  public pipeRoughnessUnit: string;
  public temperatureValidation: { min: number, max: number };
  public pipeRoughnessValidation: { min: number, max: number };
  public showDrillPipeData: boolean;
  public useMudPits = false;
  
  private _defaultPipeRoughness: number;
  private _dataBackup: any | null = null;

  public form = createForm<RunCasingAndCirculate, RunCasingAndCirculateForm>(this, {
    formType: FormType.SUB,
    formControls: {
      type: new UntypedFormControl(OperationType.RUNCASINGANDCIRCULATE),
      fluid: new UntypedFormControl(null, [Validators.required]),
      standardFluidFlow: new UntypedFormControl(null, [Validators.required]),
      tripIntoHole: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required, Validators.min(0), Validators.max(100000)] }),
      includeRiser: new UntypedFormControl(null),
      outsideDiameter: new UntypedFormControl(null, { updateOn: 'blur' }),
      insideDiameter: new UntypedFormControl(null, { updateOn: 'blur' }),
      circulationTime: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required, Validators.min(0), Validators.max(100000)] }),
      inletTemperature: new UntypedFormControl(null, { updateOn: 'blur' }),
      pipeRoughness: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      staticPeriod: new UntypedFormControl(0, { updateOn: 'blur', validators: [Validators.required, Validators.min(0), Validators.max(300)] }),
      useMudPits: new UntypedFormControl(false),
      mudPits: new UntypedFormControl(null),
      annulusIndex: new UntypedFormControl(null)
    },
    toFormGroup: (obj: RunCasingAndCirculate | null): RunCasingAndCirculateForm | null => {
      if (!obj) {
        return null;
      }

      this._dataBackup = { ...obj };

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { tripIntoHole, riser, circulationTime, pipeRoughness, inletTemperature, staticPeriod, includeRiser, useMudPits, mudPits } = obj;

      obj.fluid = this.fluids?.find(f => f.id === obj.fluid.id);
      this.showRiser = includeRiser;
      this.useMudPits = useMudPits;

      return {
        type: obj.type,
        standardFluidFlow: obj.flow,
        tripIntoHole,
        circulationTime,
        fluid: obj.fluid,
        staticPeriod,
        pipeRoughness,
        includeRiser: includeRiser,
        outsideDiameter: riser?.outsideDiameter,
        insideDiameter: riser?.insideDiameter,
        inletTemperature,
        useMudPits,
        mudPits: useMudPits ? mudPits : null,
        annulusIndex: obj.annulusIndex
      };
    },
    fromFormGroup: (formValue: RunCasingAndCirculateForm): RunCasingAndCirculate | null => {
      const { fluid, tripIntoHole, standardFluidFlow, circulationTime, outsideDiameter, insideDiameter,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        inletTemperature, pipeRoughness, staticPeriod, annulusIndex, includeRiser, useMudPits, mudPits } = formValue;

        const controls = this.form.formGroup.controls;
        const check = (control, field) => control?.invalid || (control?.touched && this._dataBackup?.[field] === formValue[field]);

        if (check(controls.tripIntoHole, 'tripIntoHole') ||
            check(controls.outsideDiameter, 'outsideDiameter') ||
            check(controls.insideDiameter, 'insideDiameter') ||
            check(controls.circulationTime, 'circulationTime') ||
            check(controls.inletTemperature, 'inletTemperature') ||
            check(controls.standardFluidFlow, 'standardFluidFlow') ||
            check(controls.pipeRoughness, 'pipeRoughness') ||
            check(controls.staticPeriod, 'staticPeriod')) {
          
          controls.tripIntoHole.markAsUntouched();
          controls.outsideDiameter.markAsUntouched();
          controls.insideDiameter.markAsUntouched();
          controls.circulationTime.markAsUntouched();
          controls.inletTemperature.markAsUntouched();
          controls.pipeRoughness.markAsUntouched();
          controls.standardFluidFlow.markAsUntouched();
          controls.staticPeriod.markAsUntouched();
          return;
        }

        this._dataBackup = formValue;

      return {
        type: OperationType.RUNCASINGANDCIRCULATE,
        flow: standardFluidFlow,
        tripIntoHole,
        includeRiser,
        riser: this.showRiser ? {
          outsideDiameter,
          insideDiameter
        } : null,
        fluid,
        circulationTime,
        inletTemperature,
        pipeRoughness,
        staticPeriod,
        useMudPits,
        mudPits,
        annulusIndex
      };
    }
  });

  async ngOnChanges(): Promise<void> {
    this.isLoading.set(true);

    this.tempUnit = this.userUnits.temperature;
    this.shortLengthUnit = this.userUnits.shortLengths == 'in' ? '"' : this.userUnits.shortLengths;
    this.weigthUnit = this.userUnits.linearDensity == 'lb/ft' ? '#' : this.userUnits.linearDensity;
    this.pipeRoughnessUnit = this.userUnits.shortLengths;

    switch (this.tempUnit) {
      case '°F':
        this.temperatureValidation = { min: 0, max: 500 };
        break;
      case '°C':
        this.temperatureValidation = { min: -17.77, max: 260 };
        break;
      case 'K':
        this.temperatureValidation = { min: 255.37, max: 533.15 };
        break;
    }
    this.form.formGroup.controls.inletTemperature.setValidators([Validators.min(this.temperatureValidation.min), Validators.max(this.temperatureValidation.max), Validators.required]);

    this._defaultPipeRoughness = 0.001968;
    if (this.userUnits.shortLengths == 'in') {
      this.pipeRoughnessValidation = { min: 0, max: 0.25 };
    } else if (this.userUnits.shortLengths == 'cm') {
      this._defaultPipeRoughness = 0.00499;
      this.pipeRoughnessValidation = { min: 0, max: 0.6 };
    } else {
      this._defaultPipeRoughness = 0.0499;
      this.pipeRoughnessValidation = { min: 0, max: 6.3 };
    }
    this.form.formGroup.controls.pipeRoughness.setValidators([Validators.min(this.pipeRoughnessValidation.min), Validators.max(this.pipeRoughnessValidation.max), Validators.required]);

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));

    this.getRunCasingData();
  }

  signalRfunc(data: any) {
    if ((data.tubularStringId && data.tubularStringId !== this.currentString.id)) {
      return;
    }

    if (data.action === PeriforOnChangeMessages.REFRESH_DRILL_STRING) {
      this.getRunCasingData();
    }
  }

  ngAfterViewInit(): void {
    if (this.form.formGroup.controls.pipeRoughness.value == null) {
      this.form.formGroup.controls.pipeRoughness.patchValue(this._defaultPipeRoughness, { emitEvent: false });
    }
    const defaultFluid = this.fluids ? this.fluids.find(x => x.id === this.annularFluidId) : null;
    if (!this.form.formGroup.controls.fluid.value) {
      this.form.formGroup.controls.fluid.setValue(defaultFluid, { emitEvent: false });
    }

    if (this.defaultRiser.includeRiser && this.form.formGroup.get('includeRiser').value == null) {
      this.form.formGroup.get('includeRiser').setValue(true, { emitEvent: false });
      this.showRiser = true;
      this.checkRiserValues();
    }
  }

  private getRunCasingData() {
    this.isLoading.set(true);
    this._drillstringService.getDrillstringForTubular().pipe(
      map((drillstring) => {
        this.landingString = drillstring['drillPipe'];
        this.landingStringMissing = !this.landingString;

        this.showDrillPipeData = this.isSubsea || this.currentString.type == 'Liner' ? true : null;
        this.selectedLandingString = this.landingString ?
          `Landing String: OD: ${this.landingString.outsideDiameter}${this.shortLengthUnit}, Weight: ${this.landingString.weightPerFoot.toFixed(2)}${this.weigthUnit}, ID: ${this.landingString.insideDiameter}${this.shortLengthUnit}, Grade: ${drillstring.drillPipeGrade?.name || 'Steel'}` :
          'Please Select a Landing String';

        const hasRiserDefault = this.isSubsea && this.currentTubularIndex > 0 ? true : false;
        const riserControl = this.form.formGroup.controls.includeRiser;
        if (riserControl.value === null) {
          riserControl.setValue(hasRiserDefault, { emitEvent: false });
          this.hasRiserChange();
        }

        this.form.formGroup.controls.inletTemperature.setValue((this.form.formGroup.value.inletTemperature ?? this.surfaceTemperature), { emitEvent: false });

        this.isLoading.set(false);
      }),
      catchError(err => {
        this.isLoading.set(false);
        return err;
      })).subscribe();
  }

  public hasRiserChange() {
    this.showRiser = this.form.formGroup.controls.includeRiser.value ?? false;

    if (this.showRiser) {
      this.form.formGroup.controls.outsideDiameter.setValidators([Validators.required]);
      this.form.formGroup.controls.insideDiameter.setValidators([Validators.required]);
    } else {
      this.form.formGroup.controls.outsideDiameter.clearValidators();
      this.form.formGroup.controls.insideDiameter.clearValidators();
    }

    this.form.formGroup.controls.outsideDiameter.updateValueAndValidity();
    this.form.formGroup.controls.insideDiameter.updateValueAndValidity();

    this.checkRiserValues();
  }

  private checkRiserValues() {
    this.form.formGroup.controls.outsideDiameter
      .setValue((this.form.formGroup.value.outsideDiameter ?? this.defaultRiser?.riser?.outsideDiameter ?? GetShortLengthValueFromInches(21, this.userUnits.shortLengths)), { emitEvent: false });

    this.form.formGroup.controls.insideDiameter
      .setValue((this.form.formGroup.value.insideDiameter ?? this.defaultRiser?.riser?.insideDiameter ?? GetShortLengthValueFromInches(19.75, this.userUnits.shortLengths)));
  }

  ngOnDestroy(): void {
    this.signalRfunc = null;
  }
}
